import type { MouseEvent } from 'react';
import { Button, useToast } from '@kcd/design-system';
import { trackEvent } from '@kcd/event-tracker';

import {
  useUpdateCartQuantity,
  useUpdateQuickQuantity,
} from '~/api/domains/cart';
import type { UpdateShoppingCartQuantityRequest } from '~/api/domains/cart';
import { getCheckoutBeforeRecommendRoute } from '~/constants/checkout/utils';
import { fetchDefaultShippingAddress } from '~/domains/address/queries';
import { useOAIInvalidateQueries } from '~/hooks/reactQuery';
import type { CART_TYPE } from '~/hooks/useCartTypeParam';
import { getOAIErrorMessage } from '~/libs/openapi';
import { OAIApiError } from '~/libs/openApiError';
import useAddCartTooltipStore from '~/store/global/useAddCartTooltipStore';
import useProductBottomSheetStore from '~/store/global/useProductBottomSheetStore';
import useProductOptionStore from '~/store/global/useProductOptionStore';
import { openPageModal } from '~/utils/mobileApp';
import { useSnackbar } from '~/app/common/components/Snackbar';

import type { ProductOption } from './types';

type Props = {
  selectedOptions: ProductOption[];
  mallName?: string;
};

export function OrderButtonContainer({ selectedOptions, mallName }: Props) {
  const { offerId } = useProductOptionStore();
  const { bottomSheetOption, closeBottomSheet } = useProductBottomSheetStore();
  const invalidateQueries = useOAIInvalidateQueries();
  const { mutateAsync: updateShoppingCartQuantity } = useUpdateCartQuantity();
  const { mutateAsync: updateQuickQuantity } = useUpdateQuickQuantity();
  const toast = useToast();
  const snackbar = useSnackbar();
  const { showTooltip } = useAddCartTooltipStore();

  // App Router: Pages 의 usePushWithQuery(url, false) 는 모두 openPageModal 로 귀결되므로 단순화
  const pushWithQuery = (nextUrl: string) =>
    openPageModal(`${nextUrl}${window.location.search}`);

  // 바로구매 장바구니에 담기
  const addQuickCart = async (
    requestBody: UpdateShoppingCartQuantityRequest,
  ) => {
    try {
      await updateQuickQuantity(requestBody);
      closeBottomSheet();

      await invalidateQueries('/api/market-place/v1/shopping-cart-items/count');
      const quantity = requestBody.stockKeepingUnits.reduce(
        (acc, curr) => acc + (curr.quantity ?? 0),
        0,
      );

      const { productName } = selectedOptions[0];
      const options = requestBody.stockKeepingUnits.map((unit) => unit);

      // 신규 바로구매용 장바구니 이벤트
      trackEvent('added to cart in 마켓', {
        cartType: '바로구매',
        placement: bottomSheetOption.placement,
        path: window.location.pathname,
        productName,
        vendorName: mallName,
        offerId,
        quantity,
        selectedOptions: options,
      });
    } catch (err) {
      if (err instanceof OAIApiError) {
        const errMessage = getOAIErrorMessage(err);
        toast(errMessage, { offset: 86 });
      }
      throw err;
    } finally {
      closeBottomSheet();
    }
  };

  // 일반 장바구니에 담기
  const addShoppingCart = async (
    requestBody: UpdateShoppingCartQuantityRequest,
  ) => {
    try {
      await updateShoppingCartQuantity(requestBody);
      closeBottomSheet();

      await invalidateQueries(
        '/api/market-place/v3/shopping-cart-items',
        '/api/market-place/v1/shopping-cart-items/count',
        '/api/market-place/v1/shopping-cart-items/total-in-shopping-cart-payment',
      );

      const quantity = requestBody.stockKeepingUnits.reduce(
        (acc, curr) => acc + (curr.quantity ?? 0),
        0,
      );

      const { productName } = selectedOptions[0];
      const options = requestBody.stockKeepingUnits.map((unit) => unit);
      // 신규 장바구니용 장바구니 이벤트
      trackEvent('added to cart in 마켓', {
        cartType: '장바구니',
        placement: bottomSheetOption.placement,
        path: window.location.pathname,
        productName,
        vendorName: mallName,
        offerId,
        quantity,
        selectedOptions: options,
      });
    } catch (err) {
      if (err instanceof OAIApiError) {
        const errMessage = getOAIErrorMessage(err);
        toast(errMessage, { offset: 86 });
      }
      throw err;
    } finally {
      closeBottomSheet();
    }
  };

  const onSubmitCTA = async (
    e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>,
    cartType: CART_TYPE,
  ) => {
    e.stopPropagation();
    const requestBody: UpdateShoppingCartQuantityRequest = {
      offerId,
      stockKeepingUnits: selectedOptions.map(({ count, skuId, price }) => ({
        skuId: skuId!,
        quantity: count,
        price,
      })),
    };

    switch (cartType) {
      case 'QUICK': {
        await addQuickCart(requestBody);
        const hasDefaultAddress = await fetchDefaultShippingAddress();
        if (!hasDefaultAddress) {
          // 바로구매 + 주소 X
          openPageModal(`/addresses/new?cartType=${cartType}`);
        } else {
          // 바로구매 + 주소 O
          const openUrl = await getCheckoutBeforeRecommendRoute(cartType);
          pushWithQuery(`${openUrl}&offerId=${offerId}`);
        }
        break;
      }
      case 'SHOPPING_CART': {
        await addShoppingCart(requestBody);

        switch (bottomSheetOption.noticeType) {
          case 'snackbar':
            snackbar({
              decoration: selectedOptions[0].productName,
              text: '장바구니에 상품을 추가했어요.',
              onClick: () => pushWithQuery('/cart'),
              buttonProps: {
                children: '장바구니 이동',
              },
            });
            break;
          case 'toast':
            void toast('장바구니에 상품을 추가했어요.', { offset: 100 });
            break;
          case 'tooltip':
            showTooltip(3000);
            break;
          default:
            void toast('장바구니에 상품을 추가했어요.', { offset: 100 });
        }

        // NOTE(MVP): cross-sell 바텀시트는 App Router 미마운트 → 분기 제외 (후속 이관)
        break;
      }
      default:
        void addShoppingCart(requestBody);
    }
  };

  return (
    <div className="mt-12">
      <div className="mt-8 flex gap-6">
        {(bottomSheetOption.purchaseType === 'all' ||
          bottomSheetOption.purchaseType === 'cart') && (
          <Button
            block
            size="lg"
            variant="outline"
            onClick={(e) => onSubmitCTA(e, 'SHOPPING_CART')}
            disabled={!selectedOptions.length}
          >
            장바구니 담기
          </Button>
        )}
        {(bottomSheetOption.purchaseType === 'all' ||
          bottomSheetOption.purchaseType === 'quick') && (
          <Button
            block
            size="lg"
            variant="primary"
            onClick={(e) => onSubmitCTA(e, 'QUICK')}
            disabled={!selectedOptions.length}
          >
            바로구매
          </Button>
        )}
      </div>
    </div>
  );
}
